import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Action,
  Alert,
  Badge,
  DateInput,
  Modal,
  MultiSelect,
  NumberInput,
  RelativeDateTime,
  SimpleSelect,
  SupportingText,
  TextAreaInput,
  TextInput,
} from 'corso-admin-ui';
import {
  CrewMerchantUi,
  helperSchema,
  inspectionGradeCodeNames,
} from 'corso-types';
import { FC, FormEventHandler, useCallback, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useBeforeUnload } from 'react-router-dom';
import { z } from 'zod';
import api from '~/api';
import DescriptionList from '~/components/DescriptionList';
import MediaUploader from '~/components/field/MediaUploader';
import { customFieldValueTypeFormatter } from '~/components/FormatCustomField';
import { MediaGallery } from '~/components/media/MediaGallery';
import { useClaim } from '~/features/claimReview/ClaimReviewProvider';
import { useConfigSettings } from '~/hooks/useConfigSettings';
import { useStoreId } from '~/providers/StoreUserProvider';
import { formatter } from 'corso-web-lib';
import LineItemDisclosure from './LineItemDisclosure';

const inspectionSettingsQueryPrefix = 'inspectionSettings';

function useClaimLineItemInspectionSettings(
  claimId: number,
  claimLineItemId: number,
) {
  const storeId = useStoreId();

  return useQuery({
    queryKey: [
      inspectionSettingsQueryPrefix,
      storeId,
      claimId,
      claimLineItemId,
    ],
    queryFn: () =>
      api
        .store(storeId)
        .claim(`${claimId}`)
        .lineItem(`${claimLineItemId}`)
        .inspection.settings(),
    refetchOnMount: true,
  });
}

type InspectionTemplate = CrewMerchantUi.InspectionTemplate;

// * this might be better put into a shared type, similar to CrewMerchantUi.CustomField
type CustomField =
  CrewMerchantUi.ClaimInspectionSettings['templates'][number]['customFields'][number];
// TODO use/get underlying enum
type CustomFieldValueType = CustomField['valueType'];

const customFieldValueTypeSchema = {
  Text: helperSchema.nonEmptyString,
  Number: z.number(),
  Date: z.string().datetime(),
  Select: helperSchema.nonEmptyString,
  MultiSelect: helperSchema.nonEmptyString,
} as const satisfies Record<CustomFieldValueType, z.ZodType<unknown>>;

type CustomFieldValueTypeData<T extends CustomFieldValueType> = z.infer<
  (typeof customFieldValueTypeSchema)[T]
>;

const toOptions = (options: string[]) =>
  options.map((option) => ({
    label: option,
    value: option,
  }));

const stringToOptions = (options: string) => {
  if (!options) return [];

  return options.split(',').map((option) => ({
    label: option,
    value: option,
  }));
};

const customFieldValueTypeInput = {
  Text: ({ customField, onChange, value, error }) => (
    <TextAreaInput // TODO consider text input vs textarea
      id={`${customField.id}`}
      label={customField.displayName}
      details={customField.description}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      error={error}
    />
  ),
  Number: ({ customField, onChange, value, error }) => {
    const parsed = Number.parseFloat(value);
    return (
      <NumberInput
        id={`${customField.id}`}
        label={customField.displayName}
        details={customField.description}
        value={!Number.isNaN(parsed) ? parsed : null}
        onChange={(e) => onChange(e.target.valueAsNumber)}
        error={error}
      />
    );
  },
  Date: ({ customField, onChange, value, error }) => (
    <DateInput
      id={`${customField.id}`}
      label={customField.displayName}
      value={value}
      details={customField.description}
      onChange={onChange}
      error={error}
    />
  ),
  Select: ({ customField, onChange, value, error }) => (
    <SimpleSelect
      label={customField.displayName}
      options={toOptions(customField.options)}
      details={customField.description}
      value={value}
      onChange={onChange}
      error={error}
    />
  ),
  MultiSelect: ({ customField, onChange, value, error }) => (
    <MultiSelect
      label={customField.displayName}
      options={toOptions(customField.options)}
      details={customField.description}
      value={stringToOptions(value)}
      onChange={(selected) =>
        onChange(selected.map(({ value: v }) => v).join(','))
      }
      error={error}
    />
  ),
} as const satisfies {
  [V in CustomFieldValueType]: FC<{
    customField: CustomField & { valueType: V };
    value: string;
    onChange: (value: CustomFieldValueTypeData<V>) => void;
    error?: string;
  }>;
};

function InspectionDisplay({
  lineItemId,
  inspection,
}: {
  lineItemId: number;
  inspection: CrewMerchantUi.ClaimLineItemInspection;
}) {
  const [showFullInspection, setShowFullInspection] = useState(false);
  const storeId = useStoreId();
  const claim = useClaim();

  const { mutate: deleteInspection, isPending } = useMutation({
    mutationFn: () =>
      api
        .store(storeId)
        .claim(`${claim.id}`)
        .lineItem(`${lineItemId}`)
        .inspection.delete(`${inspection.id}`),
    onSuccess: () => claim.refetch(),
  });

  return (
    <div className="grid grid-cols-2 gap-2 text-sm">
      <div>
        <p>{inspection.name}</p>
        <p className="text-xs text-neutral-500">
          Created <RelativeDateTime dateTime={inspection.createdOn} />
        </p>
      </div>
      <div className="flex items-center gap-2 justify-self-end">
        <Action
          icon="eye"
          accessibilityLabel={`View ${inspection.name}`}
          onClick={() => setShowFullInspection(true)}
        />
        <Modal
          show={showFullInspection}
          onClose={() => setShowFullInspection(false)}
          title={inspection.name}
        >
          <DescriptionList
            layoutType="list"
            descriptions={[
              {
                term: 'Created',
                details: <RelativeDateTime dateTime={inspection.createdOn} />,
              },
              {
                term: 'Grade',
                details: inspection.storeInspectionGrade?.name,
              },
              ...inspection.customFields.map((customField) => ({
                term: customField.displayName,
                details: customFieldValueTypeFormatter[customField.valueType](
                  customField.value,
                ),
              })),
              { term: 'Comment', details: inspection.comment },
              {
                term: 'Media',
                details: !!inspection.images.length && (
                  <MediaGallery
                    title="Inspection Media"
                    items={inspection.images.map(({ url }) => ({
                      src: url,
                      alt: 'Inspection Media',
                    }))}
                  />
                ),
              },
            ].filter(({ details }) => !!details)} // omit terms with no details
          />
        </Modal>
        <Action
          icon="remove"
          loading={isPending}
          onClick={() => deleteInspection()}
          accessibilityLabel={`Delete ${inspection.name}`}
        />
      </div>
    </div>
  );
}

/**
 * The type for the form values is different than what is needed to create the inspection
 * mainly to allow the storeInspectionGrade to be nullable for the select
 */
const createSchema = z.object({
  name: z.string().min(1),
  storeInspectionGrade: z
    .object({
      id: z.number(),
    })
    .optional(),
  comment: z.string().optional(),
  customFields: z
    .array(z.object({ id: z.number(), value: z.string() }))
    .optional(),
  images: z
    .array(
      z.object({
        name: z.string(),
        src: z.string(),
      }),
    )
    .optional(),
});

function InspectionCreate({
  lineItem,
}: {
  lineItem: CrewMerchantUi.ClaimLineItem;
}) {
  const [showForm, setShowForm] = useState(false);
  const [inspectionTemplate, setInspectionTemplate] =
    useState<InspectionTemplate>();

  const storeId = useStoreId();
  const claim = useClaim();

  const {
    data: settings,
    isPending: isSettingsPending,
    error,
  } = useClaimLineItemInspectionSettings(claim.id, lineItem.id);

  const inspectionGrades = settings?.inspectionGrades ?? [];

  const {
    mutateAsync: createInspection,
    isPending: isCreateInspectionPending,
    isError,
  } = useMutation({
    mutationFn: ({
      name,
      storeInspectionGrade,
      comment,
      customFields = [],
      images,
    }: z.infer<typeof createSchema>) =>
      api
        .store(storeId)
        .claim(`${claim.id}`)
        .lineItem(`${lineItem.id}`)
        .inspection.create({
          name,
          comment,
          customFields,
          storeInspectionGrade,
          images: images?.map(({ name: filename }) => ({
            filename,
          })),
        }),
    onSuccess: () => claim.refetch(),
  });

  const {
    control,
    register,
    formState: { errors, isDirty },
    handleSubmit,
    reset,
    setValue,
  } = useForm<z.infer<typeof createSchema>>({
    resolver: zodResolver(createSchema),
    // matches the empty array `useFieldArray` sets, which otherwise leaves the untouched form dirty
    defaultValues: { customFields: [] },
  });

  const resetFormCreate = useCallback(() => {
    setShowForm(false);
    setInspectionTemplate(undefined);
    reset();
  }, [reset]);

  const pickInspectionTemplate = useCallback(
    (template?: InspectionTemplate) => {
      setInspectionTemplate(template);
      // when selecting a template, initialize the custom fields with empty values, so one exists for each
      setValue(
        'customFields',
        template?.customFields.map(({ id }) => ({ id, value: '' })) ?? [],
      );

      // the default template is just named 'Inspection', so that, and any others similarly named will not have a default name
      const defaultName =
        template && template.name !== 'Inspection' ? template.name : '';
      setValue('name', defaultName);
    },
    [setValue],
  );

  const customFieldsFieldArray = useFieldArray({
    control,
    name: 'customFields',
  });

  const onSubmit: FormEventHandler = (event) => {
    handleSubmit(
      async (values) => {
        await createInspection(createSchema.parse(values));
        resetFormCreate();
      },
      (invalid) => {
        console.error('Invalid Form Submission', invalid);
      },
    )(event).catch(console.error);
  };

  // * This  handles the browser's beforeunload event such as a refresh
  const onBeforeUnload = useCallback(
    (event: BeforeUnloadEvent) => {
      if (!isDirty) return;

      // cancel the event as stated by the standard
      event.preventDefault();
      /** set `returnValue` to help with compatibility @see https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event#compatibility_notes */
      // oxlint-disable-next-line no-param-reassign
      event.returnValue = '';
    },
    [isDirty],
  );

  useBeforeUnload(onBeforeUnload);

  const inspectionGradeOptions = inspectionGrades
    .sort((a, b) => a.inspectionGradeCode.localeCompare(b.inspectionGradeCode))
    .map((ig) => ({
      label: (
        <div>
          {ig.name}
          <p className="text-xs text-neutral-500">
            {' '}
            {inspectionGradeCodeNames[ig.inspectionGradeCode]}
          </p>
        </div>
      ),

      value: `${ig.id}`,
    }));

  if (isSettingsPending) return null; // TODO consider a better loading state
  if (error)
    return (
      <Alert variant="critical" heading="Failed to Load Inspection Settings" />
    );

  if (!showForm) {
    return (
      <div className="self-end">
        <Action icon="add" variant="primary" onClick={() => setShowForm(true)}>
          Add Inspection
        </Action>
      </div>
    );
  }

  if (!inspectionTemplate && settings.templates.length === 1) {
    // always expected to have at least one, i.e. the default, when inspections are enabled
    pickInspectionTemplate(settings.templates[0]);
  }

  return (
    <Modal
      show // always shown if the conditional renders are met
      title={`Add ${inspectionTemplate?.name ?? 'Inspection'} for ${lineItem.originalStoreOrderLineItem.name ?? 'Claim Line Item'}`}
      onClose={resetFormCreate}
    >
      {!inspectionTemplate ? (
        <SimpleSelect
          label="Inspection Template"
          options={settings.templates
            .map((template) => ({
              // ! all inspection templates are expected to have a unique name, if not this will cause issues
              label: template.name,
              value: template.name,
            }))
            .sort((a, b) => a.label.localeCompare(b.label))}
          onChange={(selected) =>
            pickInspectionTemplate(
              settings.templates.find(({ name }) => name === selected),
            )
          }
        />
      ) : (
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          {inspectionTemplate.inspectionInstructions && (
            <Alert variant="info" heading="Instructions">
              <div className="text-left whitespace-pre-wrap">
                {inspectionTemplate.inspectionInstructions}
              </div>
            </Alert>
          )}
          <TextInput
            id="inspection-name"
            label="Name"
            required // schema validates as optional, but required for all new inspections
            {...register('name')}
            error={errors.name?.message}
          />
          {inspectionTemplate.shouldCollectInspectionGrade && (
            <Controller
              control={control}
              name="storeInspectionGrade"
              rules={{ required: 'Inspection grade is required' }}
              render={({ field: { value, onChange }, fieldState }) => (
                <SimpleSelect
                  label="Grade"
                  placeholder="Select an Inspection Grade"
                  options={inspectionGradeOptions}
                  onChange={(selected) =>
                    onChange(
                      inspectionGrades.find(
                        ({ id }) => id === Number.parseInt(selected, 10),
                      ),
                    )
                  }
                  value={`${value?.id ?? ''}`}
                  error={fieldState.error?.message}
                />
              )}
            />
          )}
          {inspectionTemplate.customFields.map(
            (inspectionTemplateCustomField, i) => {
              const CustomFieldInput =
                customFieldValueTypeInput[
                  inspectionTemplateCustomField.valueType
                ];

              return (
                <CustomFieldInput
                  key={inspectionTemplateCustomField.id}
                  customField={inspectionTemplateCustomField as never} // TODO evaluate why the never typecast is needed
                  value={customFieldsFieldArray.fields[i]?.value ?? ''}
                  onChange={(value) => {
                    if (!customFieldsFieldArray.fields[i]) {
                      // these should be initialized on template selection so one missing would be an invalid state
                      throw new Error('Unexpected Missing Custom Field');
                    }
                    customFieldsFieldArray.update(i, {
                      id: inspectionTemplateCustomField.id,
                      value: `${value}`,
                    });
                  }}
                  error={errors.customFields?.[i]?.value?.message}
                />
              );
            },
          )}
          {inspectionTemplate.showInspectionComment && (
            <TextAreaInput
              id="inspection-comment"
              label="Comment"
              {...register('comment')}
              error={errors.comment?.message}
            />
          )}
          {inspectionTemplate.showInspectionMediaUpload && (
            <Controller
              control={control}
              name="images"
              render={({ field: { value, onChange } }) => (
                <MediaUploader
                  id="inspection-uploads"
                  label="Media"
                  uploadPath="claimLineItemInspection"
                  assets={value}
                  onChange={onChange}
                />
              )}
            />
          )}
          {isError && (
            <SupportingText error>
              There was an error saving the inspection. Please try again.
            </SupportingText>
          )}
          <Action
            variant="primary"
            type="submit"
            loading={isCreateInspectionPending}
          >
            Save Inspection
          </Action>
        </form>
      )}
    </Modal>
  );
}

type InspectionProps = {
  claimLineItem: CrewMerchantUi.ClaimLineItem;
};

function shouldShowInspectionSection(
  claimType: 'Return' | 'Warranty',
  isWarrantyInspectionEnabled: boolean,
  isReturnInspectionEnabled: boolean,
) {
  if (claimType === 'Warranty' && isWarrantyInspectionEnabled) return true;

  if (claimType === 'Return' && isReturnInspectionEnabled) return true;

  return false;
}

function Inspection({ claimLineItem }: InspectionProps) {
  const claim = useClaim();

  const { data } = useConfigSettings(({ returns, warranties }) => ({
    returns,
    warranties,
  }));

  const { returns, warranties } = data ?? {};

  const showInspectionSection = shouldShowInspectionSection(
    claim.claimType,
    !!warranties?.isWarrantyInspectionEnabled,
    !!returns?.isReturnInspectionEnabled,
  );

  if (!showInspectionSection) return null;

  return (
    <LineItemDisclosure
      title="Inspections"
      supportingText={
        <Badge>{formatter.count(claimLineItem.inspections.length)}</Badge>
      }
    >
      {claimLineItem.inspections.length > 0 && (
        <ol className="flex flex-col gap-px bg-neutral-200">
          {claimLineItem.inspections.map((inspection) => (
            <li
              className="bg-white p-2 first:pt-0 last:pb-0"
              key={inspection.id}
            >
              <InspectionDisplay
                lineItemId={claimLineItem.id}
                inspection={inspection}
              />
            </li>
          ))}
        </ol>
      )}
      <InspectionCreate lineItem={claimLineItem} />
    </LineItemDisclosure>
  );
}

export default Inspection;
